<template>
  <div>
    <Head title="Productos" />
    
    <!-- Header -->
    <div class="flex justify-between items-center mb-8">
      <div class="flex items-center gap-4">
        <button 
          class="p-3 text-white bg-teal-700 hover:bg-teal-800 rounded-lg transition-colors"
          title="Volver atrás"
          @click="back()"
        >
          <i class="fa fa-arrow-left"></i>
        </button>
        <div>
          <h1 class="text-3xl font-bold text-gray-900">Catálogo de Productos</h1>
          <p class="text-sm text-gray-500 mt-1">Gestión de inventario, límites de stock, precios y referencias cruzadas</p>
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex flex-wrap items-center justify-between gap-4 mb-6">
      <Link 
        class="btn-blue flex items-center gap-2" 
        href="/products/create"
      >
        <i class="fa fa-plus"></i>
        <span>Crear Producto</span>
      </Link>

      <div class="flex flex-wrap items-center gap-3">
        <button 
          class="btn-blue flex items-center gap-2 relative transition-all"
          :class="{'bg-blue-700 ring-2 ring-blue-400': searchForm}"
          title="Buscador avanzado"
          @click="toggleSearch"
        >
          <i class="fa fa-filter"></i>
          <span>Buscador Avanzado</span>
          <span 
            v-if="activeFiltersCount > 0"
            class="ml-1 px-1.5 py-0.2 bg-white text-blue-800 rounded-full text-xs font-bold"
          >
            {{ activeFiltersCount }}
          </span>
        </button>

        <a 
          href="/products/export" 
          class="btn-blue flex items-center gap-2"
          title="Exportar productos"
        >
          <i class="fa fa-download"></i>
          <span class="hidden sm:inline">Exportar</span>
        </a>

        <button 
          type="button"
          class="flex items-center gap-2 px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors shadow-sm text-sm"
          title="Imprimir catálogo en tickera térmica con códigos de barra"
          @click="printCatalogThermal"
        >
          <i class="fa fa-barcode text-base"></i>
          <span class="hidden sm:inline">Tickera Catálogo</span>
        </button>

        <div class="relative">
          <button 
            class="btn-blue flex items-center gap-2"
            :class="{'bg-blue-700': imported}"
            @click="toggleImport"
          >
            <i class="fa fa-upload"></i>
            <span class="hidden sm:inline">Importar</span>
          </button>

          <form 
            v-if="imported"
            class="absolute top-full right-0 mt-2 p-4 bg-white rounded-lg shadow-lg z-30"
            style="width: 400px !important;"
            action="/products/import" 
            method="post"
            enctype="multipart/form-data"
          >
            <input name="_token" type="hidden" :value="csrf" />
            <div class="flex flex-col gap-3">
              <input 
                type="file" 
                name="file"
                class="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-full file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
                accept=".csv,.xlsx"
              />
              <button type="submit" class="btn-blue w-full">
                Subir archivo
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Buscador Avanzado Panel -->
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="transform -translate-y-2 opacity-0"
      enter-to-class="transform translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="transform translate-y-0 opacity-100"
      leave-to-class="transform -translate-y-2 opacity-0"
    >
      <div 
        v-show="searchForm"
        class="p-5 mb-6 bg-white rounded-xl shadow-md border border-blue-100 bg-gradient-to-br from-white to-blue-50/20"
      >
        <div class="flex items-center justify-between pb-3 mb-4 border-b border-gray-100">
          <div class="flex items-center gap-2 text-blue-900 font-bold">
            <i class="fa fa-search text-blue-600"></i>
            <span>Filtros de Búsqueda Avanzada</span>
          </div>
          <button 
            v-if="activeFiltersCount > 0"
            @click="resetFilters"
            class="text-xs text-red-600 hover:text-red-800 font-semibold flex items-center gap-1 transition-colors"
          >
            <i class="fa fa-times-circle"></i>
            Limpiar Filtros
          </button>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <!-- Términos -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Términos</label>
            <div class="relative">
              <input
                ref="searchInput"
                v-model="form.search.terms"
                type="text"
                class="form-input w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm pl-8"
                placeholder="Nombre, alias, descripción..."
              />
              <i class="fa fa-font absolute left-2.5 top-3 text-gray-400 text-xs"></i>
            </div>
          </div>

          <!-- Código o Referencia Cruzada -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Código / Ref. Cruzada</label>
            <div class="relative">
              <input
                v-model="form.search.code"
                type="text"
                class="form-input w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm pl-8 font-mono"
                placeholder="SKU, OEM o ref. cruzada..."
              />
              <i class="fa fa-barcode absolute left-2.5 top-3 text-gray-400 text-xs"></i>
            </div>
          </div>

          <!-- Categoría -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Categoría</label>
            <select
              v-model="form.search.category_id"
              class="form-select w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value="">Todas las categorías</option>
              <option 
                v-for="cat in productCategories" 
                :key="cat.id" 
                :value="cat.id"
              >
                {{ cat.name }}
              </option>
            </select>
          </div>

          <!-- Etiqueta (Tags) -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Etiqueta</label>
            <div class="relative">
              <input
                v-model="form.search.tag"
                type="text"
                class="form-input w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm pl-8"
                placeholder="Buscar por etiqueta..."
              />
              <i class="fa fa-tag absolute left-2.5 top-3 text-gray-400 text-xs"></i>
            </div>
          </div>

          <!-- Estado de Stock -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Estado de Stock</label>
            <select
              v-model="form.search.stock_status"
              class="form-select w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value="">Todos los niveles</option>
              <option value="normal">Normal (En rango)</option>
              <option value="low">⚠️ Stock Bajo (≤ Mínimo)</option>
              <option value="out">⛔ Sin Stock (Agotado)</option>
              <option value="excess">📦 Exceso de Stock (≥ Máximo)</option>
            </select>
          </div>

          <!-- Marca -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Marca</label>
            <select
              v-model="form.search.brand_id"
              class="form-select w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value="">Todas las marcas</option>
              <option 
                v-for="brand in brands" 
                :key="brand.id" 
                :value="brand.id"
              >
                {{ brand.name }}
              </option>
            </select>
          </div>

          <!-- Depósito -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Depósito / Tienda</label>
            <select
              v-model="form.search.deposit"
              class="form-select w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value="">Todos los depósitos</option>
              <option 
                v-for="dep in deposits" 
                :key="dep.id" 
                :value="dep.id"
              >
                {{ dep.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </transition>

    <!-- Quick Links -->
    <div class="mb-8">
      <h3 class="text-xl font-bold mb-4">Auxiliares</h3>
      <div class="flex flex-wrap gap-3">
        <Link 
          href="/product-categories"
          class="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
        >
          <i class="fa fa-tags"></i>
          Categorías
        </Link>
        <Link
          href="/brands" 
          class="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
        >
          <i class="fa fa-bookmark"></i>
          Marcas
        </Link>
      </div>
    </div>

    <!-- Bulk Selection Banner -->
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="transform -translate-y-2 opacity-0"
      enter-to-class="transform translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="transform translate-y-0 opacity-100"
      leave-to-class="transform -translate-y-2 opacity-0"
    >
      <div 
        v-if="selectedIds.length > 0"
        class="mb-4 p-3.5 bg-blue-50/90 border border-blue-200 rounded-xl shadow-sm flex flex-wrap items-center justify-between gap-3"
      >
        <div class="flex items-center gap-3">
          <span class="inline-flex items-center justify-center bg-blue-600 text-white text-xs font-bold rounded-full h-6 px-2 min-w-[1.5rem]">
            {{ selectedIds.length }}
          </span>
          <span class="text-sm font-semibold text-blue-900">
            {{ selectedIds.length === 1 ? '1 producto seleccionado' : `${selectedIds.length} productos seleccionados` }}
          </span>
          <button 
            type="button" 
            class="text-xs text-blue-700 hover:text-blue-900 font-medium underline ml-2 transition-colors cursor-pointer"
            @click="selectedIds = []"
          >
            Deseleccionar todos
          </button>
        </div>

        <div class="flex items-center gap-2">
          <button 
            type="button"
            @click="confirmBulkDelete"
            :disabled="isDeleting"
            class="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors cursor-pointer"
          >
            <i :class="isDeleting ? 'fa fa-spinner fa-spin' : 'fa fa-trash-alt'"></i>
            <span>Eliminar Seleccionados ({{ selectedIds.length }})</span>
          </button>
        </div>
      </div>
    </transition>

    <!-- Floating Bulk Actions Toolbar (Sticky bottom) -->
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="transform translate-y-6 opacity-0"
      enter-to-class="transform translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="transform translate-y-0 opacity-100"
      leave-to-class="transform translate-y-6 opacity-0"
    >
      <div 
        v-if="selectedIds.length > 0"
        class="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 bg-gray-900/95 backdrop-blur text-white px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-4 border border-gray-700"
      >
        <div class="flex items-center gap-2.5">
          <span class="bg-blue-600 text-white text-xs font-bold px-2.5 py-0.5 rounded-full">
            {{ selectedIds.length }}
          </span>
          <span class="text-sm font-medium">
            {{ selectedIds.length === 1 ? 'producto seleccionado' : 'productos seleccionados' }}
          </span>
        </div>

        <div class="h-4 w-px bg-gray-700"></div>

        <button 
          type="button" 
          class="text-xs text-gray-300 hover:text-white underline cursor-pointer transition-colors"
          @click="selectedIds = []"
        >
          Cancelar
        </button>

        <button 
          type="button"
          @click="confirmBulkDelete"
          :disabled="isDeleting"
          class="inline-flex items-center gap-2 px-4 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg shadow transition-colors cursor-pointer"
        >
          <i :class="isDeleting ? 'fa fa-spinner fa-spin' : 'fa fa-trash-alt'"></i>
          <span>Eliminar</span>
        </button>
      </div>
    </transition>

    <!-- Products Table -->
    <div class="bg-white rounded-lg shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-center bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wider">
            <th class="w-10 px-3 py-3 text-center">
              <input 
                type="checkbox" 
                class="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4 cursor-pointer"
                :checked="isAllSelected"
                :indeterminate.prop="isIndeterminate"
                :disabled="!products?.data?.length"
                @change="toggleSelectAll"
                title="Seleccionar todos los de esta página"
              />
            </th>
            <th class="p-3 text-left">Código / Ref</th>
            <th class="py-3 px-4 text-left">Nombre</th>
            <th class="p-3">Categoría</th>
            <th class="p-3">Marca</th>
            <th class="p-3">Inventario & Alertas</th>
            <th class="p-3">Costo</th>
            <th class="p-3">Precio</th>
            <th class="p-3">Precio Bs</th>
            <th class="p-3">Ganancia</th>
          </tr>
        </thead>
        
        <tbody class="divide-y divide-gray-100">
          <tr 
            v-for="product in products.data" 
            :key="product.id"
            :class="[
              'hover:bg-gray-50 transition-colors text-center',
              selectedIds.includes(product.id) ? 'bg-blue-50/70 font-medium' : ''
            ]"
          >
            <!-- Checkbox Selección -->
            <td class="w-10 px-3 py-3 text-center" @click.stop>
              <input 
                type="checkbox" 
                :value="product.id"
                v-model="selectedIds"
                class="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4 cursor-pointer"
              />
            </td>

            <!-- Código y Referencias Cruzadas -->
            <td class="p-3 text-left">
              <Link 
                :href="`products/${product.id}`"
                class="flex items-center gap-3 text-gray-700 hover:text-blue-600 font-medium"
              >
                <img 
                  v-if="product.photo"
                  :src="product.photo"
                  :alt="product.name"
                  class="w-9 h-9 rounded-lg object-cover shadow-sm border border-gray-200"
                />
                <div class="flex flex-col">
                  <div class="flex items-center gap-1.5">
                    <span class="font-mono font-bold text-sm text-gray-900">{{ product.code }}</span>
                    <button
                      type="button"
                      @click.prevent.stop="printProductThermal(product)"
                      class="text-gray-400 hover:text-indigo-600 p-0.5 rounded hover:bg-indigo-50 transition-colors"
                      title="Imprimir código de barra en tickera"
                    >
                      <i class="fa fa-barcode text-sm"></i>
                    </button>
                  </div>
                  <div class="flex items-center gap-1.5 mt-0.5">
                    <span 
                      v-if="product.cross_references_count > 0"
                      class="inline-flex items-center gap-1 px-1.5 py-0.2 rounded text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200"
                      title="Referencias cruzadas asociadas"
                    >
                      <i class="fa fa-exchange-alt text-[9px]"></i>
                      {{ product.cross_references_count }} ref. cruzadas
                    </span>
                  </div>
                </div>
              </Link>
            </td>

            <!-- Nombre y Tags -->
            <td class="py-3 px-4 min-w-0 text-left">
              <Link 
                :href="`products/${product.id}`"
                class="text-gray-800 font-medium hover:text-blue-600 break-words whitespace-normal block"
              >
                {{ product.name }}
              </Link>
              <div v-if="isPlanProduct(product.code)" class="mt-1">
                <Link
                  :href="`/products/${product.id}/edit`"
                  class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-800 hover:bg-blue-200 border border-blue-300 transition-colors shadow-xs"
                  title="Administrar los planes y tarifas de este producto"
                >
                  <i class="fa fa-cogs text-[10px] text-blue-600"></i>
                  <span>Administrar Planes</span>
                </Link>
              </div>
              <!-- Tags list if any -->
              <div v-if="product.tags" class="flex flex-wrap gap-1 mt-1">
                <span 
                  v-for="(tag, tIdx) in splitTags(product.tags)" 
                  :key="tIdx"
                  class="text-[10px] px-1.5 py-0.2 bg-gray-100 text-gray-600 rounded font-medium"
                >
                  #{{ tag }}
                </span>
              </div>
            </td>

            <td class="p-3 text-gray-600 text-sm">
              <span v-if="product?.product_category" class="px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-md text-xs font-medium">
                {{ product.product_category.name }}
              </span>
              <span v-else class="text-gray-400 text-xs">Sin categoría</span>
            </td>

            <td class="p-3 text-gray-600 text-sm">
              {{ product?.brand?.name || '-' }}
            </td>

            <!-- Cantidades y Alertas de Stock -->
            <td class="p-3 text-left">
              <div class="flex flex-col gap-1.5">
                <!-- Status Badge -->
                <div class="flex items-center gap-1.5">
                  <span 
                    v-if="product.stock_status === 'out_of_stock'"
                    class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-200"
                  >
                    <i class="fa fa-ban text-[10px]"></i>
                    Sin Stock (0)
                  </span>

                  <span 
                    v-else-if="product.stock_status === 'low_stock'"
                    class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-900 border border-amber-300"
                    title="Alerta: Stock por debajo o igual al mínimo configurado"
                  >
                    <i class="fa fa-exclamation-triangle text-amber-600 text-[10px]"></i>
                    Stock Bajo: {{ product.quantity }} (Mín: {{ product.min_stock }})
                  </span>

                  <span 
                    v-else-if="product.stock_status === 'excess_stock'"
                    class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-900 border border-purple-300"
                    title="Alerta: Stock excede el máximo permitido"
                  >
                    <i class="fa fa-layer-group text-purple-600 text-[10px]"></i>
                    Exceso: {{ product.quantity }} (Máx: {{ product.max_stock }})
                  </span>

                  <span 
                    v-else
                    class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800"
                  >
                    <i class="fa fa-check-circle text-green-600 text-[10px]"></i>
                    Stock Total: {{ product.quantity }}
                  </span>

                  <!-- Limits indicators if set -->
                  <span v-if="product.min_stock > 0 && product.stock_status !== 'low_stock'" class="text-[10px] text-gray-500 font-mono" title="Stock Mínimo configurado">
                    (Mín: {{ product.min_stock }})
                  </span>
                </div>

                <!-- Deposits Breakdown -->
                <div class="flex flex-wrap gap-1">
                  <span 
                    v-for="deposit in product.product_deposits"
                    :key="deposit.id"
                    class="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-800 border border-blue-100"
                  >
                    {{ deposit?.deposit?.name }}: <strong class="ml-1">{{ deposit?.quantity }}</strong>
                  </span>
                </div>
              </div>
            </td>

            <td class="p-3 text-gray-600 text-sm font-mono">
              {{ formatCurrency(product?.cost) }}
            </td>

            <td class="p-3 text-gray-700 text-sm font-mono font-semibold">
              {{ formatCurrency(product?.prices[0]?.price) }}
            </td>

            <td class="p-3 text-gray-600 text-sm font-mono">
              {{ formatCurrencyBs(product?.prices[0]?.price * Number(change)) }}
            </td>

            <td class="p-3">
              <span 
                :class="[
                  'px-2.5 py-1 rounded-full text-xs font-semibold font-mono inline-block',
                  getGainClass(product?.prices[0]?.price - product?.cost)
                ]"
              >
                {{ formatCurrency(product?.prices[0]?.price - product?.cost) }}
              </span>
            </td>
          </tr>

          <tr v-if="!products?.data?.length">
            <td 
              class="p-8 text-center text-gray-500 border-t" 
              colspan="10"
            >
              <div class="flex flex-col items-center justify-center gap-2">
                <i class="fa fa-box-open text-3xl text-gray-300"></i>
                <span class="text-sm font-medium">No se encontraron productos con los filtros seleccionados</span>
                <button 
                  v-if="activeFiltersCount > 0"
                  @click="resetFilters" 
                  class="mt-2 text-xs text-blue-600 hover:underline font-semibold"
                >
                  Limpiar filtros de búsqueda
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <pagination 
      v-if="products?.meta?.links"
      :links="products.meta.links" 
      class="mt-6"
    />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination.vue'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'

export default {
  name: 'ProductsIndex',
  
  components: {
    Pagination,
    Head,
    Icon,
    Link,
  },

  layout: Layout,

  props: {
    products: {
      type: Object,
      required: true
    },
    groups: Object,
    csrf: {
      type: String,
      required: true
    },
    brands: Object,
    parts: Object,
    models: Object,
    productCategories: {
      type: Array,
      default: () => []
    },
    deposits: Object,
    regions: Object,
    change: {
      type: Number,
      required: true
    },
    cop_change: Number
  },

  data() {
    return {
      searchForm: false,
      imported: false,
      selectedIds: [],
      isDeleting: false,
      form: this.$inertia.form({
        search: {
          terms: '',
          code: '',
          category_id: '',
          tag: '',
          stock_status: '',
          deposit: '',
          brand_id: '',
          part_id: '',
          truck_model_id: '',
          region_id: '',
        },
      }),
    }
  },

  computed: {
    isAllSelected() {
      const pageProducts = this.products?.data || []
      if (pageProducts.length === 0) return false
      return pageProducts.every(p => this.selectedIds.includes(p.id))
    },

    isIndeterminate() {
      const pageProducts = this.products?.data || []
      if (pageProducts.length === 0) return false
      const selectedOnPage = pageProducts.filter(p => this.selectedIds.includes(p.id))
      return selectedOnPage.length > 0 && selectedOnPage.length < pageProducts.length
    },

    activeFiltersCount() {
      let count = 0
      const s = this.form.search
      if (s.terms) count++
      if (s.code) count++
      if (s.category_id) count++
      if (s.tag) count++
      if (s.stock_status) count++
      if (s.deposit) count++
      if (s.brand_id) count++
      if (s.part_id) count++
      if (s.truck_model_id) count++
      if (s.region_id) count++
      return count
    }
  },

  watch: {
    form: {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get(
          '/products', 
          pickBy(this.form), 
          { preserveState: true }
        )
      }, 300),
    },
  },

  created() {
    // Si ya vienen filtros activos en la URL, abrir el panel automáticamente
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.has('search[category_id]') || urlParams.has('search[code]') || urlParams.has('search[tag]') || urlParams.has('search[stock_status]')) {
      this.searchForm = true
    }
  },

  methods: {
    back() {
      this.$inertia.visit('/')
    },

    toggleSearch() {
      this.searchForm = !this.searchForm
      if(this.searchForm) {
        this.$nextTick(() => {
          this.$refs.searchInput?.focus()
        })
      }
    },

    resetFilters() {
      this.form.search = {
        terms: '',
        code: '',
        category_id: '',
        tag: '',
        stock_status: '',
        deposit: '',
        brand_id: '',
        part_id: '',
        truck_model_id: '',
        region_id: '',
      }
    },

    toggleImport() {
      this.imported = !this.imported
    },

    splitTags(tagsStr) {
      if (!tagsStr) return []
      return tagsStr.split(',').map(t => t.trim()).filter(Boolean)
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('es-VE', {
        style: 'currency',
        currency: 'USD'
      }).format(value || 0)
    },

    formatCurrencyBs(value) {
      return new Intl.NumberFormat('es-VE', {
        style: 'currency',
        currency: 'VEF'
      }).format(value || 0)
    },

    getGainClass(gain) {
      if(gain > 0) return 'bg-green-100 text-green-800'
      if(gain < 0) return 'bg-red-100 text-red-800'
      return 'bg-gray-100 text-gray-800'
    },

    printProductThermal(product) {
      window.open(
        `/products/${product.id}/barcode-ticket?size=80mm&copies=1&autoprint=1`,
        'Ticket Código de Barra',
        'width=460,height=680,toolbar=no,menubar=no,location=no,status=no'
      );
    },

    printCatalogThermal() {
      window.open(
        '/products/thermical?size=80mm&autoprint=1',
        'Catálogo Térmico',
        'width=460,height=680,toolbar=no,menubar=no,location=no,status=no'
      );
    },

    toggleSelectAll() {
      const pageIds = (this.products?.data || []).map(p => p.id)
      if (this.isAllSelected) {
        this.selectedIds = this.selectedIds.filter(id => !pageIds.includes(id))
      } else {
        const set = new Set([...this.selectedIds, ...pageIds])
        this.selectedIds = Array.from(set)
      }
    },

    confirmBulkDelete() {
      if (this.selectedIds.length === 0) return

      const count = this.selectedIds.length
      const title = '¿Eliminar productos seleccionados?'
      const text = count === 1
        ? '¿Está seguro de que desea eliminar este producto? Podrá recuperarlo posteriormente si es necesario.'
        : `¿Está seguro de que desea eliminar los ${count} productos seleccionados? Podrá recuperarlos posteriormente si es necesario.`

      const swal = this.$swal || window.Swal

      if (swal) {
        swal.fire({
          title: title,
          text: text,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#dc2626',
          cancelButtonColor: '#6b7280',
          confirmButtonText: count === 1 ? 'Sí, eliminar' : `Sí, eliminar (${count})`,
          cancelButtonText: 'Cancelar',
          focusCancel: true
        }).then((result) => {
          if (result.isConfirmed) {
            this.executeBulkDelete()
          }
        })
      } else {
        if (confirm(text)) {
          this.executeBulkDelete()
        }
      }
    },

    executeBulkDelete() {
      this.isDeleting = true
      this.$inertia.post('/products/bulk-destroy', {
        ids: this.selectedIds
      }, {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          this.selectedIds = []
          this.isDeleting = false
        },
        onError: () => {
          this.isDeleting = false
        },
        onFinish: () => {
          this.isDeleting = false
        }
      })
    },

    isPlanProduct(code) {
      if (!code) return false
      const c = String(code).toUpperCase().trim().replace(/[\s\.\-_]/g, '')
      return c === 'RECARGAPLANES' || c === 'RECARGA2'
    },
  }
}
</script>
